import React from "react";
import { Home, Building2, PackageCheck, Sparkles, Truck, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { MediaThumb } from "@/components/MediaPanel";

const services = [
  {
    id: "residential",
    slug: "house-removals",
    icon: Home,
    title: "Packers & Movers · House Removals",
    tag: "Residential",
    image: "https://images.pexels.com/photos/7310147/pexels-photo-7310147.jpeg?auto=compress&cs=tinysrgb&w=800",
    items: ["House Removals", "Flat & Apartment Moves", "Student Moves", "Single Item Collection", "Furniture Moving"],
  },
  {
    id: "commercial",
    slug: "office-removals",
    icon: Building2,
    title: "Office Removals & Business Relocation",
    tag: "Commercial",
    image: "https://images.pexels.com/photos/7203788/pexels-photo-7203788.jpeg?auto=compress&cs=tinysrgb&w=800",
    items: ["Office Removals", "Office Relocation", "Business Moving", "Equipment Transport"],
  },
  {
    id: "packing",
    slug: "packing",
    icon: PackageCheck,
    title: "Professional Packing Services",
    tag: "Add-on",
    image: "https://images.pexels.com/photos/7464468/pexels-photo-7464468.jpeg?auto=compress&cs=tinysrgb&w=800",
    items: ["Professional Packing", "Fragile Item Packing", "Packing Materials", "Unpacking Services"],
  },
  {
    id: "cleaning",
    slug: "end-of-tenancy-cleaning",
    icon: Sparkles,
    title: "End of Tenancy & Deep Cleaning",
    tag: "Cleaning",
    image: "https://images.pexels.com/photos/4239031/pexels-photo-4239031.jpeg?auto=compress&cs=tinysrgb&w=800",
    items: ["End of Tenancy Cleaning", "Deep Cleaning", "Move-In / Move-Out Cleaning", "Office Cleaning", "Carpet Steam Clean"],
  },
  {
    id: "additional",
    slug: "man-and-van",
    icon: Truck,
    title: "Man & Van Service Cardiff",
    tag: "Flexible",
    image: null,
    items: ["Man and Van Service", "Furniture Assembly & Disassembly", "Loading & Unloading", "Local Deliveries", "Storage Assistance"],
  },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-[#F8FAFC]">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="max-w-2xl">
        <span className="text-xs tracking-[0.22em] uppercase font-bold text-[#2563EB]">What we do</span>
        <h2 className="mt-4 font-display font-semibold text-[#0A192F] text-4xl sm:text-5xl tracking-tight">
          Packers &amp; movers, cleaners and man &amp; van — all under one roof.
        </h2>
        <p className="mt-4 text-lg text-slate-600 leading-relaxed">
          Searching <span className="font-semibold text-[#0A192F]">&ldquo;removals near me&rdquo;</span> or <span className="font-semibold text-[#0A192F]">&ldquo;deep cleaning near me&rdquo;</span> in Cardiff, Newport or Swansea? You&apos;ve found the right team. Fixed prices, fully insured, same-day availability.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((s, idx) => {
          const Icon = s.icon;
          const featured = idx === 0;
          return (
            <article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              className={`hover-lift group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 flex flex-col ${
                featured ? "lg:col-span-2 lg:row-span-1" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                  {s.tag}
                </span>
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0A192F] text-white">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
              </div>
              <h3 className="mt-6 font-display font-semibold text-2xl sm:text-3xl text-[#0A192F]">{s.title}</h3>
              <ul className="mt-4 space-y-1.5 text-slate-600">
                {s.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#2563EB]" />
                    {it}
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                {s.image ? (
                  <div className="relative overflow-hidden rounded-2xl h-40 border border-slate-100">
                    <img src={s.image} alt={`${s.title} by BoxB Solutions in Cardiff`} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <MediaThumb variant={s.id} icon={Icon} />
                )}
              </div>
              <Link to={`/services/${s.slug}`} className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-[#0A192F] hover:text-[#2563EB] transition-colors">
                Learn more about {s.tag.toLowerCase()} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);
